import type { APITeoMarker } from '../TeoApi';
import { arraysEqual } from '../utility/miscFunctions';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { TeoText } from './TeoText';

export type MarkerManagerEvents = {
  markerClicked: (detail: {
    markerId: number;
    // context: ExcerptContext;
    marker: APITeoMarker;
  }) => void;
  markersFocused: (detail: { markerIds: number[] }) => void;
  markerReplacement: (detail: { markerId: number; newContent: string }) => void;
};

export class MarkerManager {
  public events = new TeoEventTarget<MarkerManagerEvents>();
  clickableMarkers: string[] = [];
  focusedMarkers: number[] = [];

  constructor(private text: TeoText) {
    this.text.ui.events.addEventListener('view', () => {
      this.setMarkers([], []);
    });
  }

  setMarkers(markers: string[], clickableMarkers: string[] = []) {
    const cMarkers = Array.from(new Set(markers));
    const cClickable = Array.from(new Set(clickableMarkers));
    const clickableMarkersChanged = !arraysEqual(this.clickableMarkers, cClickable);
    this.clickableMarkers = cClickable;
    markers = Array.from(new Set([...cMarkers, ...cClickable]));
    this.text.ui.update('markers', markers, clickableMarkersChanged);
  }

  addMarkers(markers: string[], clickableMarkers: string[] = [], resetClickable = false) {
    const updatedMarkers = [...this.text.ui.state.markers, ...markers];
    const clickable =
      resetClickable ? clickableMarkers : [...this.clickableMarkers, ...clickableMarkers];
    this.setMarkers(updatedMarkers, clickable);
  }

  removeMarkers(markers: string[]) {
    const clickableMarkers = this.clickableMarkers.filter((marker) => !markers.includes(marker));
    const updatedMarkers = this.text.ui.state.markers.filter(
      (marker) => !markers.includes(marker) && !clickableMarkers.includes(marker),
    );
    this.setMarkers(updatedMarkers, clickableMarkers);
  }

  checkMarkerVisibility(markerName: string): boolean {
    if (!this.text.ui.state.markers) return false;
    return this.text.ui.state.markers.includes(markerName);
  }

  syncPerformanceMarkers() {
    if (
      this.text.analysis === null ||
      !this.text.ui.state.dimension ||
      !this.text.ui.state.textProperty
    ) {
      this.setMarkers([]);
      return;
    }
    const markers = this.text.analysis.getCurrentTextPropertyMarkerNames();
    this.setMarkers(markers ?? []);
  }

  clickMarker(markerId: number, marker: APITeoMarker) {
    this.events.triggerEvent('markerClicked', {
      markerId,
      marker,
    });
  }

  replaceMarkerContent(markerId: number, newContent: string) {
    this.events.triggerEvent('markerReplacement', {
      markerId,
      newContent,
    });
  }

  focusMarkers(markerIds: number[]) {
    this.focusedMarkers = markerIds;
    this.events.triggerEvent('markersFocused', {
      markerIds,
    });
  }

  addFocusedMarker(markerId: number) {
    if (!this.focusedMarkers.includes(markerId)) {
      this.focusedMarkers.push(markerId);
      this.events.triggerEvent('markersFocused', {
        markerIds: this.focusedMarkers,
      });
    }
  }

  removeFocusedMarker(markerId: number) {
    const index = this.focusedMarkers.indexOf(markerId);
    if (index !== -1) {
      this.focusedMarkers.splice(index, 1);
      this.events.triggerEvent('markersFocused', {
        markerIds: this.focusedMarkers,
      });
    }
  }

  clearFocusedMarkers() {
    this.focusedMarkers = [];
    this.events.triggerEvent('markersFocused', {
      markerIds: [],
    });
  }
}
